import {
  GET_SHARED_PREFERENCE,
  HANDLE_HEADER,
} from "core-lib/constants/nativeBridge";
import { Source, VENDOR_ID_SKU } from "core-lib/constants/sourceSKU";
import { Cookies } from "core-lib/constants/storage";
import { PLATFORM } from "core-lib/data/common";
import { DictType } from "core-lib/types/Helper";
import { getCookieValue } from "core-lib/utils/cookie";
import { checkMobileOS, isDataValid, isObject } from "core-lib/utils/helpers";
import {
  passMessageToNative,
  waitForCustomEvent,
} from "core-lib/utils/nativeBridge";
import { isMGWebView, isParkPlusWebView } from "core-lib/utils/source";
import { isValidJson } from "core-lib/utils/utils";
import ip from "icepick";
import { PaymentApp } from "../constants/PaymentApp";
import { PaymentInstrument } from "../constants/PaymentInstrument";
import { PaymentPartner } from "../constants/PaymentPartner";
import { LocalStorage } from "../constants/Storage";
import { APP_INFO, NativeApps } from "../constants/Upi";
import { BankType } from "../types/Bank";
import { AppliedPromo, CardType, MethodData, Origin } from "../types/Model";
import { removeEmptyValues } from "./removeEmptyValues";

export function retryPageRedirectionUrl(
  paymentLocalData: DictType,
  retryPaymentData: DictType | null
): DictType {
  let retry: DictType | null = null;

  if (paymentLocalData) {
    switch (paymentLocalData.source) {
      case Source.CarWash: {
        if (retryPaymentData?.retryUrl) {
          retry = retryPaymentData?.retryUrl;
        }
        break;
      }

      case Source.ValetNew: {
        if (retryPaymentData?.retryUrl) {
          retry = retryPaymentData?.retryUrl;
        }
        break;
      }

      case Source.CarWax: {
        if (retryPaymentData?.retryUrl) {
          retry = retryPaymentData?.retryUrl;
        }
        break;
      }

      case Source.ParkplusStore: {
        if (retryPaymentData?.retryUrl) {
          retry = retryPaymentData?.retryUrl;
        }
        break;
      }

      case Source.IOCL: {
        if (retryPaymentData?.retryUrl) {
          retry = retryPaymentData?.retryUrl;
        }
        break;
      }

      case Source.BillPayment: {
        if (retryPaymentData?.retryUrl) {
          retry = retryPaymentData?.retryUrl;
        }
        break;
      }

      case Source.Vouchers: {
        if (retryPaymentData?.retryUrl) {
          retry = retryPaymentData?.retryUrl;
        }
        break;
      }

      case Source.UcdSubscription: {
        if (retryPaymentData?.retryUrl) {
          retry = retryPaymentData?.retryUrl;
        }
        break;
      }

      case Source.PayPlus: {
        if (retryPaymentData?.retryUrl) {
          retry = retryPaymentData?.retryUrl;
        }
        break;
      }

      case Source.PickAndDrop: {
        if (retryPaymentData?.retryUrl) {
          retry = retryPaymentData?.retryUrl;
        }
        break;
      }

      case Source.CstTagReplacement: {
        if (retryPaymentData?.retryUrl) {
          retry = retryPaymentData?.retryUrl;
        }
        break;
      }

      case Source.Challan: {
        if (retryPaymentData?.retryUrl) {
          retry = retryPaymentData?.retryUrl;
        }
        break;
      }

      case Source.SocietyReactivation: {
        if (retryPaymentData?.retryUrl) {
          retry = retryPaymentData?.retryUrl;
        }
        break;
      }

      case Source.DriverOnDemand: {
        if (retryPaymentData?.retryUrl) {
          retry = retryPaymentData?.retryUrl;
        }
        break;
      }

      case Source.RcTransfer: {
        if (retryPaymentData?.retryUrl) {
          retry = retryPaymentData?.retryUrl;
        }
        break;
      }

      default:
        break;
    }
  }
  return retry;
}

export async function getUpiAppForOptions() {
  const apps = [];
  const platform = checkMobileOS();
  let nativeMethodsArr: string[] = [
    NativeApps.GooglePay,
    NativeApps.Phonepe,
    NativeApps.PaytmUpi,
  ];

  if (![PLATFORM.ios, PLATFORM.android].includes(platform)) {
    return apps; // Unsupported platform
  }

  if (isParkPlusWebView()) {
    try {
      passMessageToNative(GET_SHARED_PREFERENCE, null, {
        key: "upi_apps",
        callbackScript: `
        window.dispatchEvent(new CustomEvent('upi_apps_event', { detail: {res:JSON.stringify(__value__)} }));
      `,
      });
      const apps: any = await waitForCustomEvent("upi_apps_event");
      passMessageToNative(GET_SHARED_PREFERENCE, null, {
        key: "app_detail_data_key",
        callbackScript: `
        window.dispatchEvent(new CustomEvent('app_detail_data_key_event', { detail: {res:JSON.stringify(__value__)} }));
      `,
      });
      const appDetail: any = await waitForCustomEvent(
        "app_detail_data_key_event"
      );
      if (apps?.detail?.res && appDetail?.detail?.res) {
        const availableApps = isValidJson(apps?.detail?.res)
          ? JSON.parse(apps?.detail?.res)?.availableApps
          : [];
        const supportedApps = isValidJson(appDetail?.detail?.res)
          ? JSON.parse(appDetail?.detail?.res)?.supported_upi_apps
          : {};
        const availableAppsPackages: Set<string> = new Set(
          availableApps.map((item) => item.packageName)
        );
        nativeMethodsArr = Object.keys(supportedApps).reduce((acc, key) => {
          const packageNames = supportedApps[key];
          if (
            packageNames.some((pn: string) => availableAppsPackages.has(pn))
          ) {
            acc.push(key);
          }
          return acc;
        }, []);
        console.log(nativeMethodsArr);
      }
    } catch (error) {
      console.error("Error accessing shared preference:", error);
    }
  } else if (isMGWebView()) {
    const nativeMethods = getCookieValue(Cookies.UpiMethods);
    if (isDataValid(nativeMethods)) {
      nativeMethodsArr = nativeMethods.split(",").map((m) => m?.toLowerCase());
    }
  }

  for (const method of nativeMethodsArr) {
    if (APP_INFO?.[method]?.package_name?.[platform]) {
      const obj = {
        method_name: APP_INFO[method]?.method_name || "",
        package_name: APP_INFO[method]?.package_name?.[platform] || "",
      };
      apps.push(obj);
    } else {
      console.warn(`Missing or invalid app info for method: ${method}`);
    }
  }
  return apps;
}

export function renderBackBtn(paymentPartner: PaymentPartner) {
  const FAIL_URL_PARTNER_MAP = {
    [PaymentPartner.Payu]: "/app/payu/fail",
    [PaymentPartner.RazorPay]: "/app/razorpay/callback",
  };
  const { origin: pageOrigin } = global.location;
  let url = FAIL_URL_PARTNER_MAP[paymentPartner.toLowerCase()] ?? "";
  const config = {
    show: true,
    title: "Payment",
    backBtn: {
      show: true,
      behaviour: "injectScript",
      script: `
        (function (){
        if(document.getElementById('confirmationDialog')) return; 
  const overlay = document.createElement("div");
  overlay.id = "confirmationOverlay";
  overlay.style.position = "fixed";
  overlay.style.top = "0";
  overlay.style.left = "0";
  overlay.style.width = "100%";
  overlay.style.height = "100%";
  overlay.style.backgroundColor = "rgba(0, 0, 0, 0.5)";
  overlay.style.zIndex = "9998";

  // create dialog
  const confirmationDialog = document.createElement("div");
  confirmationDialog.id = "confirmationDialog";
  confirmationDialog.style.display = "none";
  confirmationDialog.style.position = "fixed";
  confirmationDialog.style.top = "50%";
  confirmationDialog.style.left = "50%";
  confirmationDialog.style.transform = "translate(-50%, -50%)";
  confirmationDialog.style.backgroundColor = "#fff";
  confirmationDialog.style.border = "1px solid #ccc";
  confirmationDialog.style.padding = "12px";
  confirmationDialog.style.zIndex = "9999";
  confirmationDialog.style.width = "200px";
  confirmationDialog.style.borderRadius = "4px";
  confirmationDialog.style.boxShadow = "0px 0px 10px rgba(0, 0, 0, 0.5)";

  const confirmationText = document.createElement("p");
  confirmationText.textContent = "Do you really want to leave?";
  confirmationText.style.marginBottom = "12px";
  confirmationDialog.appendChild(confirmationText);

  const confirmButton = document.createElement("button");
  confirmButton.textContent = "Yes";
  confirmButton.style.backgroundColor = "#0012ff";
  confirmButton.style.color = "#fff";
  confirmButton.style.border = "none";
  confirmButton.style.borderRadius = "4px";
  confirmButton.style.padding = "0px 11px";
  confirmButton.style.height = "32px";
  confirmButton.style.width = "80px";
  confirmButton.style.marginRight = "10px";
  confirmationDialog.appendChild(confirmButton);

  const cancelButton = document.createElement("button");
  cancelButton.textContent = "No";
  cancelButton.style.backgroundColor = "#db1e24";
  cancelButton.style.color = "#fff";
  cancelButton.style.border = "none";
  cancelButton.style.height = "32px";
  cancelButton.style.width = "80px";
  cancelButton.style.borderRadius = "4px";
  cancelButton.style.padding = "0px 11px";
  confirmationDialog.appendChild(cancelButton);
  document.body.appendChild(overlay);
  document.body.appendChild(confirmationDialog);
    cancelButton.addEventListener("click", () => {
    confirmationDialog.remove();
    overlay.remove();
  });

  confirmButton.addEventListener("click", () => {
    console.info('redirecting to','${pageOrigin}${url}');
    window.location.replace('${pageOrigin}${url}');
    confirmationDialog.style.display = "none";
    overlay.style.display = "none";
  });
  overlay.style.display = "block";
  confirmationDialog.style.display = "block";
})()

      `,
    },
  };
  if (isParkPlusWebView()) passMessageToNative(HANDLE_HEADER, null, config);
}

export function getAppsPresent(upiApps: any[]) {
  const apps = [];
  if (upiApps.find((app) => app.method_name === NativeApps.CredUpi))
    apps.push(NativeApps.CredPay);
  return apps;
}

export function getPaymentLocalData(): any {
  const paymentLocalData = global.localStorage.getItem(
    LocalStorage.PaymentLocalData
  );
  if (paymentLocalData) {
    return JSON.parse(paymentLocalData);
  }
  return null;
}

export function setPaymentLocalData({
  origin,
  orderId,
  amount,
  selectedMethod,
  appliedPromo,
}: {
  origin: Origin;
  orderId: string | number;
  amount: number;
  selectedMethod: MethodData;
  appliedPromo: AppliedPromo;
}) {
  const localData = { ...ip.thaw(origin) };
  const method = JSON.parse(JSON.stringify(selectedMethod));
  if (method?.cardInfo?.cvv) {
    method.cardInfo.cvv = null;
  }
  localData.data.id = orderId;
  localData.data.amount = amount;
  localData.data.selectedMethod = method;
  localData.data.appliedPromo = appliedPromo;
  if (!isObject(localData.data)) localData.data = {};
  global.localStorage.setItem(
    LocalStorage.PaymentLocalData,
    JSON.stringify(localData)
  );
}

export function removePaymentLocalData(): void {
  global.localStorage.removeItem(LocalStorage.PaymentLocalData);
}

export function createRetryPaymentData(data) {
  global.localStorage.setItem(
    LocalStorage.PaymentRetryPaymentData,
    JSON.stringify(data)
  );
}

export function getRetryPaymentData(): any {
  const retryData = global.localStorage.getItem(
    LocalStorage.PaymentRetryPaymentData
  );
  if (retryData) {
    return JSON.parse(retryData);
  }
  return null;
}

export function removeRetryPaymentData(): void {
  global.localStorage.removeItem(LocalStorage.PaymentRetryPaymentData);
}

export function isRetry() {
  const retryUrl = getRetryPaymentData()?.retryUrl ?? null;
  const selectedMethod = getPaymentLocalData()?.data?.selectedMethod ?? null;
  return !!(retryUrl && selectedMethod);
}

export function parseBankAsSelectedMethod(bank: BankType): MethodData {
  return {
    eligibilityApiUrl: null,
    paymentPartner: bank.paymentPartner as PaymentPartner,
    paymentInstrument: PaymentInstrument.Netbanking,
    paymentApp: PaymentApp.Netbanking,
    displayName: bank.displayName,
    description: "",
    icon: bank.icon,
    id: bank.displayName,
    paymentByNative: null,
    bankCode: bank.code,
    cardInfo: null,
  };
}
export const formatCardDisplayName = (cardNumber: string) => {
  const result = `****${cardNumber.slice(-4)}` || "";
  return result.toString();
};
export function parseCardAsSelectedMethod(card: CardType): MethodData {
  return {
    eligibilityApiUrl: null,
    paymentPartner: card?.paymentPartner,
    paymentInstrument: PaymentInstrument.Card,
    paymentApp: card?.paymentApp,
    displayName: card?.owner ?? card.name,
    description: formatCardDisplayName(card.number),
    icon: card.icon,
    id: "Credit/Debit Card",
    paymentByNative: null,
    bankCode: null,
    cardInfo: card,
  };
}
export function formatCardPattern(val: string) {
  return val
    ?.replace(/\s?/g, "")
    ?.replace(/(\d{4})/g, "$1 ")
    ?.replace(/[^0-9 ]/g, "")
    ?.trim();
}

export function fetchVendorData(source: string) {
  const vendorIdSku = VENDOR_ID_SKU[source];
  const vendorId = Number(vendorIdSku?.vendorId);
  const sku = vendorIdSku?.sku;
  return { sku, vendorId, vendorIdSku };
}

export function preparePaymentDetails({
  selectedMethod,
  finalAmount,
  wallet,
  walletBalance,
  isCashOrder,
}) {
  let paymentDetails: any = {
    selected_payment_method: [],
  };

  if (selectedMethod && finalAmount) {
    paymentDetails.selected_payment_method.push(
      selectedMethod.paymentInstrument
    );
    paymentDetails.payment_partner_name = selectedMethod.paymentPartner;
    paymentDetails.payment_method = selectedMethod.paymentInstrument;
    paymentDetails.payment_partner_icon = selectedMethod.icon;
    paymentDetails.payment_app = selectedMethod.paymentApp;
    paymentDetails.display_name = selectedMethod.displayName;

    if (selectedMethod.paymentInstrument === PaymentInstrument.Card) {
      paymentDetails = {
        ...paymentDetails,
        card_type: selectedMethod.cardInfo.network,
        iin: selectedMethod.cardInfo.iin,
        card_category: selectedMethod.cardInfo.type,
        last_four: selectedMethod.cardInfo.number.slice(-4),
        nickname: selectedMethod.cardInfo.nickname,
        save_payment_method: !!selectedMethod.cardInfo.save,
        cp: selectedMethod.cardInfo.cp,
        saved_card_id: selectedMethod.cardInfo.savedCardId,
      };
    } else if (
      selectedMethod.paymentInstrument === PaymentInstrument.Netbanking
    ) {
      paymentDetails = {
        ...paymentDetails,
        bank_code: selectedMethod.bankCode,
        bank_display_name: selectedMethod.displayName,
        bank_logo: selectedMethod.icon,
      };
    } else if (
      selectedMethod.paymentPartner === PaymentPartner.Simpl &&
      selectedMethod.eligibilityResponse?.token
    ) {
      paymentDetails.simpl_token = selectedMethod.eligibilityResponse.token;
    }
  }

  if (
    wallet.enabled &&
    wallet.isApplied &&
    walletBalance?.balance &&
    !isCashOrder &&
    finalAmount
  ) {
    paymentDetails.selected_payment_method.push(
      PaymentInstrument.ParkplusWallet
    );
    paymentDetails.wallet_amount = walletBalance.balance;
  }

  if (!finalAmount || isCashOrder) {
    const instrument = isCashOrder
      ? PaymentInstrument.Cash
      : PaymentInstrument.ParkplusWallet;
    const partner = isCashOrder ? PaymentPartner.Cash : PaymentPartner.Parkplus;
    if (wallet.isApplied && wallet.enabled) {
      paymentDetails.wallet_amount = walletBalance?.balance;
    }
    paymentDetails = {
      ...paymentDetails,
      payment_partner_icon: null,
      payment_app: null,
      display_name: null,
      payment_method: instrument,
      selected_payment_method: [
        ...paymentDetails.selected_payment_method,
        instrument,
      ],
      payment_partner_name: partner,
    };
  }

  return paymentDetails;
}

export async function createMethodRequestBody({ origin, itemDetails }) {
  const { vendorIdSku } = fetchVendorData(origin.source);
  const upiApps = await getUpiAppForOptions();
  const apps = getAppsPresent(upiApps);
  const obj: any = {
    upi_apps: upiApps,
    pay_via_app_present: apps,
    item_details: itemDetails ?? undefined,
    vendor_id: vendorIdSku?.vendorId ?? undefined,
    biller_id: origin?.data?.billerId ?? undefined,
    sku: origin?.data?.sku ?? vendorIdSku?.sku ?? undefined,
  };
  return removeEmptyValues(obj);
}

export function createUserPromoRequestBody({
  origin,
  itemDetails,
  selectedMethod,
  amount,
  finalAmount,
  wallet,
  walletBalance,
}) {
  const { vendorIdSku } = fetchVendorData(origin.source);
  const isCashOrder = !!origin?.data?.isCash;
  const body: any = {
    price: amount ?? undefined,
    vendor_id: vendorIdSku?.vendorId ?? undefined,
    biller_id: origin?.data?.billerId ?? undefined,
    sku: origin?.data?.sku ?? vendorIdSku?.sku ?? undefined,
    source: origin?.data?.source ?? undefined,
    currency: origin?.data?.currency ?? undefined,
    project_id: origin?.data?.projectId ?? undefined,
    location_id:
      origin?.data?.locationId ?? origin?.data?.projectId ?? undefined,
    vehicle_number:
      origin?.data?.vehicleNumber ?? origin?.data?.vehicle_number ?? undefined,
  };
  if (itemDetails?.length && itemDetails?.[0]?.unit_price > 0) {
    body.item_details = itemDetails;
    body.sku = itemDetails[0]?.sku;
  }
  body.payment_details = preparePaymentDetails({
    selectedMethod,
    finalAmount,
    wallet,
    walletBalance,
    isCashOrder,
  });

  return removeEmptyValues(body);
}

export function createFetchPromoRequestBody({
  origin,
  itemDetails,
  selectedMethod,
  finalAmount,
  amount,
  wallet,
  walletBalance,
}) {
  const { vendorIdSku } = fetchVendorData(origin.source);
  const isCashOrder = !!origin?.data?.isCash;
  const body: any = {
    partner_name: "parkwheels",
    price: amount ?? undefined,
    vendor_id: vendorIdSku?.vendorId ?? undefined,
    biller_id: origin?.data?.billerId ?? undefined,
    sku: origin?.data?.sku ?? vendorIdSku?.sku ?? undefined,
    source: origin?.data?.source ?? undefined,
    currency: origin?.data?.currency ?? undefined,
    project_id: origin?.data?.projectId ?? undefined,
    location_id: origin?.data?.projectId ?? undefined,
    vehicle_number:
      origin?.data?.vehicleNumber ?? origin?.data?.vehicle_number ?? undefined,
  };
  if (itemDetails.length) {
    body.item_details = itemDetails;
    body.sku = itemDetails[0]?.sku;
  }
  body.payment_details = preparePaymentDetails({
    selectedMethod,
    finalAmount,
    wallet,
    walletBalance,
    isCashOrder,
  });
  return removeEmptyValues(body);
}

export function createSummaryRequestBody({
  origin,
  itemDetails,
  promo,
  appliedPromo,
  invalidPromoMsg,
  tip,
  addOns,
  selectedMethod,
  finalAmount,
  wallet,
  walletBalance,
  fullfilmentDetails,
}) {
  const { vendorIdSku } = fetchVendorData(origin.source);
  const isCashOrder = !!origin?.data?.isCash;
  const body: any = {};
  if (vendorIdSku?.vendorId) {
    body.category_id = vendorIdSku?.vendorId;
  }
  if (promo.enabled && appliedPromo?.code && !invalidPromoMsg) {
    body.promocode_validate = appliedPromo.rawData;
  }
  if (itemDetails?.length) {
    body.item_details = itemDetails;
  }
  if (tip) {
    body.tipping_details = {
      sku: tip.sku,
      amount: tip.amount,
    };
  }
  if (addOns.length) {
    addOns.forEach((item) => {
      body[item.key] = item.value;
    });
  }
  if (
    origin?.data?.isAmountVisible !== undefined ||
    origin?.data?.showBannerFooter !== undefined ||
    origin?.data?.showMlConsumption !== undefined
  ) {
    body.configurations = {
      is_amount_visible: origin?.data?.isAmountVisible ?? true,
      show_banner_footer: origin?.data?.showBannerFooter ?? true,
      show_ml_consumption: origin?.data?.showMlConsumption ?? false,
    };
  }
  body.fulfilment_details = fullfilmentDetails || {};
  body.payment_details = preparePaymentDetails({
    selectedMethod,
    finalAmount,
    wallet,
    walletBalance,
    isCashOrder,
  });

  return removeEmptyValues(body);
}

export async function createPromoValidationRequestBody({
  origin,
  amount,
  finalAmount,
  code,
  selectedMethod,
  wallet,
  walletBalance,
  itemDetails,
  additionalData,
}) {
  const { vendorIdSku } = fetchVendorData(origin.source);
  const isCashOrder = !!origin?.data?.isCash;
  const upiApps = await getUpiAppForOptions();
  const apps = getAppsPresent(upiApps);
  let body: any = {
    upi_apps: upiApps,
    pay_via_app_present: apps,
    vendor_id: vendorIdSku?.vendorId ?? undefined,
    biller_id: origin?.data?.billerId ?? undefined,
    sku: origin?.data?.sku ?? vendorIdSku?.sku ?? undefined,
    source: origin?.data?.source ?? undefined,
    currency: origin?.data?.currency ?? undefined,
    project_id: origin?.data?.projectId ?? undefined,
    location_id:
      origin?.data?.locationId ?? origin?.data?.projectId ?? undefined,
    vehicle_number:
      origin?.data?.vehicleNumber ?? origin?.data?.vehicle_number ?? undefined,
    price: amount ?? undefined,
    promo_code: code ?? undefined,
    payment_method: selectedMethod?.paymentInstrument ?? undefined,
    payment_partner_name: selectedMethod?.paymentPartner ?? undefined,
    method_name:
      selectedMethod?.paymentInstrument === PaymentInstrument.Card
        ? selectedMethod?.cardInfo?.iin
        : selectedMethod?.paymentApp,
    bank_code: selectedMethod?.bankCode ?? undefined,
    wallet_applied: wallet?.isApplied && !!walletBalance?.balance,
  };

  if (selectedMethod?.paymentInstrument === PaymentInstrument.Card) {
    body.last_four =
      selectedMethod.cardInfo.number.substring(
        selectedMethod?.cardInfo?.number?.length - 4
      ) ?? undefined;
  }

  if (itemDetails.length) {
    body.item_details = itemDetails;
    body.sku = itemDetails[0].sku;
  }

  // source specific config
  if (origin.source === Source.FastagRecharge) {
    body.quantity = 1;
  }

  if (additionalData) body = { ...body, ...additionalData };

  body.payment_details = preparePaymentDetails({
    selectedMethod,
    finalAmount,
    wallet,
    walletBalance,
    isCashOrder,
  });
  return removeEmptyValues(body);
}

export function createDefaultOrderRequestBody({
  origin,
  appliedPromo,
  invalidPromoMsg,
  selectedMethod,
  finalAmount,
  wallet,
  walletBalance,
}) {
  const obj: any = {};
  const isCashOrder = origin.data?.isCash;
  if (appliedPromo?.code && !invalidPromoMsg) {
    obj.promo_code = appliedPromo.code;
    obj.promocode_validate = appliedPromo?.rawData?.promocode_validate;
  }
  obj.payment_details = preparePaymentDetails({
    selectedMethod,
    finalAmount,
    wallet,
    walletBalance,
    isCashOrder,
  });

  return removeEmptyValues(obj);
}
